import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { InputPEDetailInfo } from '../../shared/component/headless/input/InputBody';
import { DisplayAlignment, DisplaySize, ImageStyle, PEInfoType, TextAlignment } from '../../shared/enum/EnumRepository';
import { WorkDetailData } from '../../shared/dto/EntityRepository';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { getFlexGrow, ImgContentInfoWrapperLeft, InfoWrapperLeft, isLeftAligned, isLeftAlignedOnly } from './WorkInfoLeft';
import MobileBanWrapper from '../../shared/component/MobileBanWrapper';
import { DETAIL_DESCRIPTION_MAX_LENGTH } from '../../shared/constants/limits';


export interface DetailProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  imageStyle: ImageStyle;
  infoType: PEInfoType;
  data: WorkDetailData;
}

const DetailInfoLeft: React.FC<DetailProps> = ({ peId, alignment, displaySize, imageStyle, infoType, data }) => {
  const handleChange = useProjectElementListStore((s) => s.updateDetail);

  const defaultAlignment = alignment || DisplayAlignment.CENTER;
  const defaultDisplaySize = displaySize || DisplaySize.BIG;
  const defaultImageStyle = imageStyle || ImageStyle.LANDSCAPE;
  const leftAligned = useMemo(
    () => isLeftAligned(defaultAlignment, defaultDisplaySize, defaultImageStyle, infoType),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle, infoType]
  );

  const leftAlignedOnly = useMemo(
    () => isLeftAlignedOnly(defaultAlignment, defaultDisplaySize, defaultImageStyle),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle]
  );

  const flexGrow = useMemo(
    () => getFlexGrow(defaultAlignment, defaultDisplaySize, defaultImageStyle),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle]
  );

  return (
    <MobileBanWrapper>
      <ImgContentInfoWrapperLeft $flexGrow={flexGrow} $leftAligned={leftAligned} $leftAlignedOnly={leftAlignedOnly}>
        {infoType !== PEInfoType.NONE && (
          <InfoWrapperLeft
            $leftAlignedOnly={leftAlignedOnly}
            $displaySize={defaultDisplaySize}
            $imageStyle={defaultImageStyle}
          >
            <DescriptionWrapper $leftAlignedOnly={leftAlignedOnly}>
              <MoleculeInputDiv
                value={data.description}
                placeholder={"Describe this detail — material, texture, etc."}
                handleChange={(e) => handleChange(peId, { description: e.target.value })}
                inputStyle={InputPEDetailInfo}
                StyledDiv={Description}
                alignment={TextAlignment.RIGHT}
                maxLength={DETAIL_DESCRIPTION_MAX_LENGTH}
              />
            </DescriptionWrapper>
          </InfoWrapperLeft>
        )}
      </ImgContentInfoWrapperLeft>
    </MobileBanWrapper>
  );
};

const DescriptionWrapper = styled.div<{ $leftAlignedOnly: boolean }>`
  display: ${({ $leftAlignedOnly }) => ($leftAlignedOnly ? 'none' : 'flex')};
  width: 100%;
`;

const Description = styled.h2`
  width: 100%;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: right;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

export default DetailInfoLeft;
